import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Button, LoadingSpinner, SearchInput, SearchableSelect, InlineAlert } from '../components/BaseComponents'
import PageHeadings from '../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../components/BaseComponents/PageTemplate/PageTemplate'
import routes from '../components/Router/routes'
import { useAppDispatch, useAppSelector } from '../redux/hooks/reduxHooks'
import {
	ACCOUNTINGPERIODS_UPDATE_RESET,
	selectAccountingPeriods,
	selectAccountingPeriodsError,
	selectAccountingPeriodsStatus,
	selectAccountingPeriodsUpdateError,
	selectAccountingPeriodsUpdateStatus,
} from '../redux/slices/accountingPeriodsSlice'
import {
	getAccountingPeriods,
} from '../redux/actions/accountingPeriodsActions'
import { default as ISICSelector } from '../components/BaseComponents/ISICSelector/ISICSelector'
import { FetchStatus, PostStatus } from '../types/LoadingStates'
import { useEffect, useRef, useState } from 'react'
import { PurchaseUploadState } from '../enums/PurchaseUploadState'
import { countries } from '../assets/countryList'
import { centsToCurrency } from '../lib/utils'
import {
	GET_PURCHASES_SUPPLIERS_LIMIT_SCOPE3,
} from '../redux/actions/carbonAccountsSupplierActions'
import Pagination from '../components/BaseComponents/Pagination/Pagination'
import { useDebouncedSearch } from '../hooks/useDebouncedSearch'
import { refineSuppliers } from '../redux/actions/updateBulkSuppliersAction'
import {
	selectUpdateBulkSuppliersError,
	selectUpdateBulkSuppliersStatus,
	UPDATE_BULK_SUPPLIERS_RESET,
} from '../redux/slices/updateBulkSuppliersSlice'
import { getScope3Suppliers } from '../redux/actions/refinementsActions'
import { REFINEMENTS_SCOPE3_SUPPLIERS_RESET, selectRefinementsscope3SupplierItems, selectRefinementsscope3SuppliersError, selectRefinementsscope3SupplierStatus } from '../redux/slices/refinementsSlice'
import { PaginationMeta } from '../types/PaginationMeta'
import { SupplierRefinementUpdate } from '../types/PurchaseItem'
import useCurrencyFormatter from '../hooks/useCurrencyFormatter'
import useApi from '../hooks/useApi'
import useCompleteAccountingStage from '../hooks/useCompleteAccountingStage'
import useRefinementRequest from '../hooks/useRefinementRequest'
import useInterruptibleRefinementSave from '../hooks/useInterruptibleRefinementSave'
import { multiFieldSubstringFilter } from '../utils/dropdownFilters'
import NumericText from '../components/BaseComponents/NumericText/NumericText'

export const CompanyRefineSuppliers = () => {
	const formatCurrency = useCurrencyFormatter();
	const countryList = countries
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const api = useApi();
	const { accountingPeriodId } = useParams()

	const accountingPeriodsStatus = useAppSelector(selectAccountingPeriodsStatus)
	const accountingPeriodsError = useAppSelector(selectAccountingPeriodsError)
	const allAccountingPeriodData = useAppSelector(selectAccountingPeriods)

	const suppliersLoadStatus = useAppSelector(selectRefinementsscope3SupplierStatus)
	const suppliersError = useAppSelector(selectRefinementsscope3SuppliersError)
	const [searchText, setSearchtext] = useState('')

	// Find the accounting period to match the url param
	const thisAccountingPeriod = allAccountingPeriodData.filter(
		(period) => period.id === accountingPeriodId
	)[0]
	const [errorNoPeriod, setErrorNoPeriod] = useState(false)

	const limit = GET_PURCHASES_SUPPLIERS_LIMIT_SCOPE3
	const [companyId, setCompanyId] = useState('')
	const [accountingPeriodIdVal, setAccountingPeriodIdVal] = useState('')

	const supplierData = useAppSelector(selectRefinementsscope3SupplierItems)
	const defaultPagination: PaginationMeta = {
		current_page: 1,
		first_page: 1,
		last_page: 1,
		per_page: 20,
		total: 0,
	}

	const { suppliers, pagination } = supplierData || { suppliers: [], pagination: defaultPagination }

	const [dirtySuppliers, setDirtySuppliers] = useState<SupplierRefinementUpdate[]>([])

	const updateSuppliersStatus = useAppSelector(selectUpdateBulkSuppliersStatus)
	const updateSuppliersError = useAppSelector(selectUpdateBulkSuppliersError)
	const completionStatus = useAppSelector(selectAccountingPeriodsUpdateStatus)
	const completionError = useAppSelector(selectAccountingPeriodsUpdateError)
	const finishing = completionStatus === PostStatus.sending
	const request = useRefinementRequest(api, accountingPeriodId)
	const lastQuery = useRef({ page: 1, searchText: '' })
	const startSave = useInterruptibleRefinementSave(api, accountingPeriodId)
	const loadSuppliers = (query: typeof lastQuery.current) => {
		if (!thisAccountingPeriod) {
			setErrorNoPeriod(true)
			return
		}
		lastQuery.current = query
		request.start((signal) => dispatch(getScope3Suppliers(
			api, thisAccountingPeriod.id, query.page, 'totalSpend', 'desc', limit, query.searchText, { signal }
		)))
	}
	const completeImport = useCompleteAccountingStage(PurchaseUploadState.scope3Refined, () => {
		dispatch(UPDATE_BULK_SUPPLIERS_RESET())
		dispatch(ACCOUNTINGPERIODS_UPDATE_RESET())
		navigate(generatePath(routes.companyCarbonAccounts.path, { accountingPeriodId }))
	})
	const saveSuppliers = () => {
		if (!thisAccountingPeriod) {
			setErrorNoPeriod(true)
			return
		}
		const { signal, onSubmitted, finish } = startSave()
		const save = async () => {
			const saved = await dispatch(refineSuppliers(api, dirtySuppliers, thisAccountingPeriod.id, { signal, onSubmitted }))
			finish()
			if (saved && !signal.aborted) completeImport(accountingPeriodId)
		}
		save()
	}

	useEffect(() => {
		dispatch(UPDATE_BULK_SUPPLIERS_RESET())
		dispatch(ACCOUNTINGPERIODS_UPDATE_RESET())
		setDirtySuppliers([])
		setSearchtext('')
	}, [api, accountingPeriodId, dispatch])

	// Data loading
	useEffect(() => {
		// Fetch accounting periods if they aren't in the store, we need to compare against the IDs
		if (accountingPeriodsStatus === FetchStatus.initial) {
			dispatch(getAccountingPeriods(api))
		}

		// // Reset the purchases store if it's currently filled
		if (suppliersLoadStatus === FetchStatus.loaded) {
			dispatch({
				type: REFINEMENTS_SCOPE3_SUPPLIERS_RESET,
			})
		}

		if (accountingPeriodsStatus === FetchStatus.loaded) {
			// If there's no accounting period defined, the url parameter is probably wrong, show an error
			if (typeof thisAccountingPeriod !== 'undefined') {
				setErrorNoPeriod(false)
				setCompanyId(thisAccountingPeriod.company_id)
				setAccountingPeriodIdVal(thisAccountingPeriod.id)
				loadSuppliers({ page: 1, searchText: '' })
			} else {
				setErrorNoPeriod(true)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountingPeriodsStatus, api, accountingPeriodId])

	const runSearch = () => {
		if (companyId !== '' && accountingPeriodIdVal !== '') {
			loadSuppliers({ page: pagination.first_page, searchText })
		}
	}
	const { searchInProgress, flush: flushSearch } = useDebouncedSearch(searchText, runSearch)
	const getIsicValue = (isicCode?: string | null) => {
		return isicCode ? [isicCode] : []
	}

	const updateSupplierJurisdictionCode = (id: string, val: any) => {
		let update = dirtySuppliers.find((dirty) => dirty.id === id)
		if(update) {
			update.jurisdictionCode = val.value;
		} else {
			update = {
				id: id,
				jurisdictionCode: val.value,
				isicCode: suppliers.find((suppler) => suppler.supplierId === id)?.industryCode || undefined,
			}
		}
		setDirtySuppliers([
			...dirtySuppliers.filter(supplier => supplier.id !== id),
			update,
		])
	}
	const updateSupplierIsicCode = (id: string, val: any) => {
		let update = dirtySuppliers.find((dirty) => dirty.id === id)
		if(update) {
			update.isicCode = val;
		} else {
			update = {
				id: id,
				isicCode: val,
				jurisdictionCode: suppliers.find((suppler) => suppler.supplierId === id)?.jurisdictionCode || undefined,
			}
		}
		setDirtySuppliers([
			...dirtySuppliers.filter(supplier => supplier.id !== id),
			update,
		])
	}

	return (
		<PageTemplate>
			<Helmet>
				<title>{routes.companyRefineSuppliers.name}</title>
			</Helmet>
			<PageHeadings
				crumbs={[
					{ label: 'Purchases', route: routes.companyPurchases.path },
					{ label: routes.companyAddAccountingPeriods.menuName as string, route: routes.companyAddAccountingPeriods.path },
					{ label: 'Refine Suppliers - Emissions generated' },
				]}
			/>
			{/* Helpful information */}
			<div className='rounded-lg bg-sun-500 p-4 my-5'>
				<p className='mb-5'>Add an industry sector code to each of your suppliers.</p>
				<p className='mb-5'>
					This will give you the approximate CO₂e emissions figures for your purchases.
				</p>
				<p>You can also add codes later.</p>
			</div>

			<div className='items-start'>
				<SearchInput
					name='searchPurchases'
					placeholder='Search'
					value={searchText}
					onChange={(event) => {
						setSearchtext(event.target.value)
					}}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							flushSearch()
						}
					}}
					onClear={() => {
						setSearchtext('')
						loadSuppliers({ page: pagination.first_page, searchText: '' })
					}}
					isLoading={searchInProgress}
					className='max-w-2xl'
				/>
			</div>
			{request.current && accountingPeriodsStatus === FetchStatus.loaded && !!thisAccountingPeriod && suppliersLoadStatus === FetchStatus.loaded &&
				updateSuppliersStatus !== PostStatus.sending && updateSuppliersStatus !== PostStatus.sent &&
				!finishing && !completionError &&
				suppliers.length > 0 && (
					<>
						{/* data display */}
						<div>
							{suppliers.map((supplier) => {
								const dirtySupplier = dirtySuppliers.find(dirty => dirty.id === supplier.supplierId)
								return (
									<div className='bg-charcoal-700 my-4' key={supplier.supplierId}>
										<div className='flex justify-between items-center mb-4 bg-charcoal-800 p-2 px-3'>
											<h2 className='text-white text-lg'>{supplier.supplierName}</h2>
											<p className='text-white text-sm ml-4'>
												Total Spend:{' '}
												{supplier.totalSpend ? <NumericText value={formatCurrency(centsToCurrency(supplier.totalSpend) || 0)} /> : ''}
											</p>
										</div>

										<div className='grid grid-cols-2 gap-5 p-2 px-3 pt-0'>
											<div>
												<p className='mt-1 text-charcoal-100 mb-2'>
													Jurisdiction of this supplier:
												</p>
												<SearchableSelect
													key={supplier.supplierId}
													id={supplier.supplierName}
													options={countryList}
													isMulti={false}
													isSearchable={true}
													filterOption={multiFieldSubstringFilter}
													isClearable={false}
													className='w-full'
													onChange={(value) => {
														updateSupplierJurisdictionCode(supplier.supplierId, value)
													}}
													value={countryList.filter(function (option) {
														return option.value === (dirtySupplier ? dirtySupplier.jurisdictionCode : supplier.jurisdictionCode)
													})}
												/>
											</div>

											<div>
												<p className='text-charcoal-100 mb-2'>
													Nature of business - Sector Code (ISIC):
												</p>
												<ISICSelector
													darkMode
													defaultValue={getIsicValue(dirtySupplier ? dirtySupplier.isicCode : supplier.industryCode)}
													min={1}
													max={1}
													updateHandler={(value) => {
														if (value.length !== 0) {
															updateSupplierIsicCode(supplier.supplierId, value[0])
														}
													}}
												/>
											</div>
										</div>
									</div>
								)
							})}
						</div>
						<Pagination
							pagination={pagination}
							onPageChange={(newPageNumber) => {
								loadSuppliers({ page: newPageNumber, searchText })
							}}
						/>
						<div className='flex flex-row-reverse mt-2'>
							<Button
								type='Primary'
								text='Complete Import'
								hasTrailingIcon
								icon='fa-solid fa-chevron-right'
								onClick={saveSuppliers}
							/>
						</div>
					</>
				)}
			{request.current && accountingPeriodsStatus === FetchStatus.loaded && !!thisAccountingPeriod && suppliersLoadStatus === FetchStatus.loaded &&
				updateSuppliersStatus !== PostStatus.sending && updateSuppliersStatus !== PostStatus.sent &&
				!finishing && !completionError &&
				suppliers.length === 0 && (
					<>
						<div className='rounded-lg bg-charcoal-700 p-4 mt-4'>
							<p className='text-white'>No supplier found.</p>
							<Button
								type='Primary'
								text='Complete Import'
								hasTrailingIcon
								icon='fa-solid fa-chevron-right'
								additionalClass='mt-5 mb-2'
								onClick={saveSuppliers}
							/>
						</div>
					</>
				)}

			{/* Errors and loading states*/}
			{accountingPeriodsStatus === FetchStatus.loading && (
				<LoadingSpinner loadingText='Loading accounting periods...' />
			)}
			{suppliersLoadStatus === FetchStatus.loading && (
				<LoadingSpinner loadingText='Loading purchases...' />
			)}
			{updateSuppliersStatus === PostStatus.sending && (
				<LoadingSpinner loadingText='Sending suppliers...' />
			)}
			{finishing && <LoadingSpinner loadingText='Completing import...' />}
			{completionStatus === PostStatus.error && (
				<InlineAlert type='error' className='mt-4'>
					<p>Supplier changes were saved, but the import could not be completed.</p>
					<p>{completionError}</p>
					<Button type='Primary' text='Retry completion' additionalClass='mt-4' onClick={() => completeImport(accountingPeriodId)} />
				</InlineAlert>
			)}
			{accountingPeriodsStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching the accounting periods list. If this problem persists,
						please contact the Neoni team.
					</p>
					<p className='text-white'>{accountingPeriodsError}</p>
					<Button type='Primary' text='Retry loading accounting periods' onClick={() => dispatch(getAccountingPeriods(api))} />
				</InlineAlert>
			)}
			{request.current && suppliersLoadStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching the purchases for this accounting period (
						{accountingPeriodId}). If this problem persists, please contact the Neoni team.
					</p>
					<p className='text-white'>{suppliersError}</p>
					<Button type='Primary' text='Retry loading suppliers' onClick={() => loadSuppliers(lastQuery.current)} />
				</InlineAlert>
			)}
			{updateSuppliersStatus === PostStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error updating the suppliers. If this problem persists, please contact the
						Neoni team.
					</p>
					<p className='text-white mb-5'>{updateSuppliersError}</p>
					<Button
						type='Primary'
						text='Retry updating suppliers'
						hasTrailingIcon
						icon='fa-solid fa-rotate-right'
						disabled={!request.current || suppliersLoadStatus !== FetchStatus.loaded}
						onClick={saveSuppliers}
					/>
				</InlineAlert>
			)}
			{errorNoPeriod && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error whilst trying to find purchases for that accounting period ID (
						{accountingPeriodId}). If this problem persists, please contact the Neoni team.
					</p>
					<Button
						onClick={() => {
							navigate(generatePath(routes.companyRefineEmissionsGenerated.path, {accountingPeriodId}), {
								replace: true,
							})
						}}
						type='Primary'
						hasTrailingIcon={true}
						icon='fa-solid fa-rotate-right'
						text='Try again'
						additionalClass='mt-9 mr-5'
					/>
					<Button
						type='Outlined'
						text='Back to accounting periods'
						onClick={() => {
							navigate(routes.companyAddAccountingPeriods.path)
						}}
						additionalClass='mt-6 mr-5'
					/>
				</InlineAlert>
			)}
		</PageTemplate>
	)
}

export default CompanyRefineSuppliers
